import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Radio } from "antd";
import CustomModal from "../../../../Core/Components/Modal/CustomModal";
import CustomInput from "../../../../Core/Components/CustomInput";
import Button from "../../../../Core/Components/CustomButton";
import CloseIcon from "../../../../Common/Svgs/CloseIcon";
import ReplaceIcon from "../../../../Common/Svgs/ReplaceIcon";
import { trikeReplacementReasonsList } from "../../../Requests/constants";
import {
  requestTrikeReplacement,
  getTrikeBreakdownByTrip,
} from "../../../../Redux/APIs/trikeBreakdownAPI";
import { updateRequest } from "../../../../Redux/Slices/requests.slice";

const ReplaceTrikeModal = ({
  visible,
  onCancel,
  isRental = false,
  tripId,
  rentalId,
}) => {
  const dispatch = useDispatch();

  const { activeBreakdownRequest, currentTrip } = useSelector(
    (state) => state.trips,
  );
  const { currentRental } = useSelector((state) => state.rentals);

  const [disabled, setDisabled] = useState("");
  const [selectedReason, setSelectedReason] = useState({});
  const [otherInput, setOtherInput] = useState({
    focus: false,
    value: "",
  });

  const handleCancelAndClose = () => {
    setDisabled("");
    setSelectedReason({});
    setOtherInput({
      focus: false,
      value: "",
    });
    onCancel();
  };

  const handleSelection = (reason) => {
    setSelectedReason(reason);
    setDisabled("");
  };

  const handleOtherTextInput = (e) => {
    setOtherInput((prev) => ({
      ...prev,
      value: e.target.value,
    }));
    setSelectedReason((prev) => ({
      ...prev,
      text: e.target.value,
    }));
    setDisabled("");
  };

  const handleReplace = async () => {
    if (!selectedReason?.id) {
      setDisabled("Please specify a reason");
      return;
    }

    if (selectedReason?.id === 5 && !otherInput.value.trim()) {
      setDisabled("Please specify a reason");
      return;
    }

    const reasonText =
      selectedReason?.id === 5
        ? otherInput.value.trim()
        : selectedReason?.text;

    dispatch(updateRequest({ key: "loading", value: true }));

    const activeId = isRental
      ? rentalId || currentRental?.id
      : tripId || currentTrip?.id;

    if (activeBreakdownRequest?.id) {
      await dispatch(
        requestTrikeReplacement({
          id: activeBreakdownRequest.id,
          replacementReason: reasonText,
          replacementHubId:
            activeBreakdownRequest?.hubId ||
            activeBreakdownRequest?.hub_id ||
            undefined,
        }),
      );

      if (activeId) {
        dispatch(
          getTrikeBreakdownByTrip(
            isRental ? { rentalId: activeId } : { tripId: activeId },
          ),
        );
      }
    }

    dispatch(updateRequest({ key: "loading", value: false }));
    handleCancelAndClose();

    dispatch(
      updateRequest({
        key: "showToastMessage",
        value: {
          visible: true,
          data: {
            title: "1 Replacement Ticket Raised",
            description: "Replacement ticket was raised successfully.",
          },
        },
      }),
    );
  };

  return (
    <CustomModal
      visible={visible}
      onCancel={handleCancelAndClose}
      width="536px"
    >
      <div
        className="p-4 bg-background-white rounded-xl"
        style={{
          boxShadow:
            "12px 12px 16px -4px rgba(16, 24, 40, 0.08), -4px -4px 6px -2px rgba(16, 24, 40, 0.03)",
        }}
      >
        <div className="flex items-center justify-between">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#E8F3F4]">
            <ReplaceIcon size={24} strokeColor="#0C4E55" />
          </div>

          <button
            type="button"
            className="mr-2"
            onClick={handleCancelAndClose}
          >
            <CloseIcon className="stroke-dark-gray w-6 h-6" />
          </button>
        </div>

        <div className="mt-5 px-2 pb-2">
          <h1 className="font-poppins text-base font-medium text-black">
            Replace Trike
          </h1>

          <p className="mt-2 font-aileron text-xs leading-[14.5px] font-normal text-black">
            Create a replacement ticket for this trike.
          </p>

          <p
            className={`mt-5 font-aileron text-xs leading-[14.5px] font-normal ${
              disabled ? "text-alert-red" : "text-dark-gray"
            }`}
          >
            Please specify a reason
          </p>

          <div
            className={`mt-2 bg-white border ${
              disabled ? "border-alert-red" : "border-light-grey"
            } rounded-xl`}
          >
            {trikeReplacementReasonsList.map((item, index) => {
              const showOtherTextInput =
                item.id === 5 && selectedReason?.id === 5;

              return (
                <div
                  key={item.id}
                  role="button"
                  className={`flex items-center gap-x-3 px-3 py-5 group hover:bg-background ${
                    index + 1 === trikeReplacementReasonsList.length
                      ? ""
                      : "border-b border-light-grey"
                  }`}
                  style={{
                    borderTopLeftRadius: index === 0 ? "12px" : null,
                    borderTopRightRadius: index === 0 ? "12px" : null,
                    borderBottomLeftRadius:
                      index + 1 === trikeReplacementReasonsList.length
                        ? "12px"
                        : null,
                    borderBottomRightRadius:
                      index + 1 === trikeReplacementReasonsList.length
                        ? "12px"
                        : null,
                  }}
                  onClick={() => handleSelection(item)}
                >
                  <Radio checked={selectedReason?.id === item.id} />

                  {item.id === 5 && showOtherTextInput ? (
                    <CustomInput
                      intent="sessionReject"
                      text="generalSettings"
                      value={otherInput.value}
                      focus={otherInput.focus}
                      border={otherInput.focus ? "success" : "none"}
                      placeholder={
                        otherInput.focus
                          ? null
                          : "Please specify the reason.."
                      }
                      onFocus={() =>
                        setOtherInput((prev) => ({
                          ...prev,
                          focus: true,
                        }))
                      }
                      onBlur={() =>
                        setOtherInput((prev) => ({
                          ...prev,
                          focus: false,
                        }))
                      }
                      onChange={handleOtherTextInput}
                    />
                  ) : (
                    <p
                      className={`font-aileron font-normal text-sm leading-[14.5px] group-hover:text-black ${
                        selectedReason?.id === item.id
                          ? "text-black font-medium"
                          : "text-dark-gray"
                      }`}
                    >
                      {item.text}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-11 flex items-center justify-end gap-x-3">
            <Button
              colors="grey"
              size="primary"
              onClick={handleCancelAndClose}
            >
              Cancel
            </Button>

            <Button
              colors="primary"
              size="primary"
              onClick={handleReplace}
            >
              Replace Trike
            </Button>
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

export default ReplaceTrikeModal;
